import React, { useEffect, useState } from 'react';
import { Button, Icon } from '@shopify/polaris';
import {
  TeamIcon,
  ChatIcon,
  StarFilledIcon,
  CheckCircleIcon,
  QuestionCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { openSupport } from '../../support';

/**
 * The two panels between the plan cards and the Free strip: what other people
 * say on the left, the questions that stop a purchase on the right.
 *
 * Kept in its own file so the billing page takes a two-line diff.
 *
 * Colours are Polaris 13 tokens, by value, because the surfaces are hand-drawn
 * divs (the house style - nothing in this app uses Polaris <Card>).
 *
 * NOTE ON ICONS: Polaris's .Polaris-Icon carries `margin: auto`. A bare <Icon>
 * used as a direct flex child soaks up all the free space and shunts whatever
 * follows it to the far end of the row. Every Icon below is wrapped in a sized,
 * flex-none box - do not "simplify" that away.
 */

const INK = '#303030';
const MUTED = '#616161';
const BORDER = '#e3e3e3';
const BORDER_SOFT = '#ebebeb';
const SURFACE_2 = '#f7f7f7';
const SUCCESS_BG = '#cdfee1';
const SUCCESS_TEXT = '#0c5132';
const STAR = '#ffb800';
const LINK = '#005bd3';

/**
 * Real figures, taken from this product's Shopify App Store listing:
 *   https://apps.shopify.com/zon-staff-management/reviews
 *
 * This is the page where someone enters payment, so everything here has to be
 * something a customer actually wrote or the listing actually shows. Rules:
 *   - `rating` and `count` must match the listing. Re-check them when it
 *     changes; a stale number is still a wrong number.
 *   - Quotes are VERBATIM. Never fix a customer's spelling, tighten their
 *     phrasing, or merge two reviews. A leading "..." marks an excerpt that
 *     starts mid-sentence - that is the only permitted edit, and it removes
 *     words rather than adding them.
 *   - `stars` is that review's own rating, not a decoration.
 *   - `author` is the store name as the listing shows it. No invented job
 *     titles and no photographs; the avatar is the store's initial, which
 *     claims nothing.
 *
 * THE SOURCE IS NAMED ON SCREEN, and that is not optional. This app is sold
 * outside the Shopify App Store, so a reader here may have no Shopify store at
 * all. The reviews are about this product and every one of them is checkable at
 * the link, but presenting them without saying which storefront they came from
 * would imply they were left by customers of this version. The "Built for
 * Shopify" badge that sits beside these figures in the Shopify app is left out
 * altogether: it is awarded to that listing, not to this install.
 */
export const REVIEWS = {
  rating: 4.8,
  count: 46,
  listingUrl: 'https://apps.shopify.com/zon-staff-management/reviews',
  quotes: [
    {
      body:
        '…powerful app for team and task management, from time keeping, reporting, ' +
        'task completion tracking and payroll, you name it.',
      author: 'JustLikePapa',
      location: 'South Africa',
      stars: 5,
    },
    {
      body:
        'The customer service is very attentive. You speak with a real human and not ' +
        'an AI bot which is lovely! They have been very helpful',
      author: "Maya's Brigadeiro",
      location: 'United States',
      stars: 5,
    },
    {
      body: 'So easy to use. Thank you so much!',
      author: 'Canon Art',
      location: 'United States',
      stars: 5,
    },
  ],
};

const ROTATE_MS = 6500;

const CARD = 'flex h-full flex-col rounded-[12px] border bg-white p-4 shadow-[0_1px_0_rgba(0,0,0,0.05)]';

/** Icon on a round success-tinted ground. The wrapper contains Polaris's margin:auto. */
function IconDisc({ source, size = 32 }) {
  return (
    <span
      className="flex-none inline-flex items-center justify-center rounded-full"
      style={{ width: size, height: size, backgroundColor: SUCCESS_BG }}
    >
      <Icon source={source} tone="success" />
    </span>
  );
}

/** Bare 16px icon, for inline use beside text. */
function InlineIcon({ source, tone = 'success', color }) {
  return (
    <span
      className="flex-none inline-flex h-4 w-4 items-center justify-center"
      style={color ? { color } : undefined}
    >
      <Icon source={source} tone={color ? 'inherit' : tone} />
    </span>
  );
}

function Stars({ rating, size = 12 }) {
  const filled = Math.round(rating);

  return (
    <span className="inline-flex items-center" aria-hidden="true">
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className="inline-flex items-center justify-center"
          style={{ width: size, height: size, color: i < filled ? STAR : '#d4d4d4' }}
        >
          <Icon source={StarFilledIcon} tone="inherit" />
        </span>
      ))}
    </span>
  );
}

function CarouselArrow({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex-none inline-flex h-6 w-6 items-center justify-center rounded-full border-0 bg-transparent p-0 transition-colors hover:bg-[#f1f1f1]"
    >
      <span className="inline-flex h-5 w-5 items-center justify-center">
        <Icon source={icon} tone="base" />
      </span>
    </button>
  );
}

function QuoteCard({ quote, t }) {
  const initial = (quote.author || '?').trim().charAt(0).toUpperCase();

  return (
    <blockquote
      className="m-0 flex min-h-[88px] flex-1 items-center gap-3 rounded-[10px] border px-3.5 py-3"
      style={{ borderColor: BORDER_SOFT, backgroundColor: SURFACE_2 }}
    >
      <span
        className="flex-none inline-flex h-10 w-10 items-center justify-center rounded-full text-[15px] font-semibold"
        style={{ backgroundColor: SUCCESS_BG, color: SUCCESS_TEXT }}
        aria-hidden="true"
      >
        {initial}
      </span>

      <div className="min-w-0">
        <Stars rating={quote.stars} />
        <p className="mb-0 mt-1 text-[13px] italic leading-snug" style={{ color: INK }}>
          {`“${quote.body}”`}
        </p>
        <p className="mb-0 mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[12px] leading-none">
          <span className="font-semibold" style={{ color: INK }}>{quote.author}</span>
          <span style={{ color: '#c9cccf' }} aria-hidden="true">&middot;</span>
          <span className="inline-flex items-center gap-1" style={{ color: SUCCESS_TEXT }}>
            <InlineIcon source={CheckCircleIcon} />
            {t('pricing_trust_verified')}
          </span>
          <span style={{ color: '#c9cccf' }} aria-hidden="true">&middot;</span>
          <span style={{ color: MUTED }}>{quote.location}</span>
        </p>
      </div>
    </blockquote>
  );
}

/** One item in the proof strip. Links out, because every figure here is checkable. */
function Proof({ icon, children, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-[12.5px] font-medium no-underline hover:underline"
      style={{ color: INK }}
    >
      <InlineIcon source={icon} />
      {children}
    </a>
  );
}

function TrustCard({ t }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = REVIEWS.quotes.length;

  const step = (delta) => setIndex((i) => (i + delta + total) % total);

  // Gentle auto-rotate; stops while someone is reading or has hold of it.
  useEffect(() => {
    if (paused || total < 2) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % total), ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, total]);

  return (
    <div className={CARD} style={{ borderColor: BORDER }}>
      <div className="flex items-center gap-2.5">
        <IconDisc source={TeamIcon} />
        <h3 className="mb-0 text-[15px] font-bold leading-tight" style={{ color: INK }}>
          {t('pricing_trust_title')}
        </h3>
      </div>

      {/* Names the storefront the figure was earned on, and links to it. */}
      <a
        href={REVIEWS.listingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1.5 block pl-[42px] text-[12.5px] leading-snug no-underline hover:underline"
        style={{ color: MUTED }}
      >
        {t('pricing_trust_subtitle', { rating: REVIEWS.rating, reviews: REVIEWS.count })}
      </a>

      <div
        className="mt-3 flex flex-1 items-center gap-1"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        {total > 1 && (
          <CarouselArrow icon={ChevronLeftIcon} label={t('pricing_trust_prev_review')} onClick={() => step(-1)} />
        )}
        <QuoteCard quote={REVIEWS.quotes[index]} t={t} />
        {total > 1 && (
          <CarouselArrow icon={ChevronRightIcon} label={t('pricing_trust_next_review')} onClick={() => step(1)} />
        )}
      </div>

      {total > 1 && (
        <div className="mt-2 flex justify-center gap-1.5">
          {REVIEWS.quotes.map((q, i) => (
            <button
              key={q.author}
              type="button"
              aria-label={String(i + 1)}
              onClick={() => setIndex(i)}
              className="h-1.5 w-1.5 rounded-full border-0 p-0 transition-colors"
              style={{ backgroundColor: i === index ? INK : '#d4d4d4' }}
            />
          ))}
        </div>
      )}

      <div
        className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-t pt-3"
        style={{ borderColor: BORDER_SOFT }}
      >
        <Proof icon={ChatIcon} href={REVIEWS.listingUrl}>
          {t('pricing_trust_stat_reviews_value', { reviews: REVIEWS.count })}
        </Proof>
        <Proof icon={StarFilledIcon} href={REVIEWS.listingUrl}>
          {t('pricing_trust_stat_rating_value', { rating: REVIEWS.rating })}
        </Proof>
      </div>
    </div>
  );
}

function FaqItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="rounded-[10px] border" style={{ backgroundColor: SURFACE_2, borderColor: BORDER_SOFT }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-3 border-0 bg-transparent px-3.5 py-2.5 text-left"
      >
        <span className="text-[13px] font-semibold leading-tight" style={{ color: INK }}>
          {question}
        </span>
        <span
          className="flex-none inline-flex h-5 w-5 items-center justify-center transition-transform duration-200"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }}
        >
          <Icon source={ChevronDownIcon} tone="base" />
        </span>
      </button>

      {isOpen && (
        <p className="mb-0 mt-0 px-3.5 pb-3 pr-10 text-[12.5px] leading-relaxed" style={{ color: MUTED }}>
          {answer}
        </p>
      )}
    </div>
  );
}

function FaqCard({ t }) {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);

  // Every answer sits directly above a Subscribe button, so it has to stay true
  // of what THIS app does. The Shopify copy of this file answers all four in
  // terms of Shopify's approval screen and its trial days; billing here is
  // Stripe Checkout and there is no trial at all - createCheckoutSession builds
  // the session from inline price_data with no trial_period_days, and an inline
  // price cannot carry a dashboard trial either. Saying otherwise on the page
  // where someone hands over a card is the kind of surprise that becomes a
  // chargeback.
  const items = [
    { id: 'plans', q: t('pricing_faq_q_change_plan'), a: t('pricing_faq_a_change_plan') },
    { id: 'trial', q: t('pricing_faq_q_trial'), a: t('pricing_faq_a_trial') },
    { id: 'extra', q: t('pricing_faq_q_extra_staff'), a: t('pricing_faq_a_extra_staff') },
    { id: 'cancel', q: t('pricing_faq_q_cancel'), a: t('pricing_faq_a_cancel') },
  ];

  return (
    <div className={CARD} style={{ borderColor: BORDER }}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <IconDisc source={QuestionCircleIcon} />
          <h3 className="mb-0 text-[15px] font-bold leading-tight" style={{ color: INK }}>
            {t('pricing_faq_title')}
          </h3>
        </div>

        {/* Routed, not an href: a plain link would reload the whole SPA. */}
        <a
          href="/Faq"
          onClick={(e) => {
            e.preventDefault();
            navigate('/Faq');
          }}
          className="flex-none text-[12.5px] font-medium no-underline hover:underline"
          style={{ color: LINK }}
        >
          {t('pricing_faq_view_all')}
        </a>
      </div>

      {/* mb-3 is the guaranteed gap above the help panel; the panel's mt-auto
          only adds to it when the card has spare height. */}
      <div className="mb-3 mt-3 flex flex-col gap-1.5">
        {items.map((item) => (
          <FaqItem
            key={item.id}
            question={item.q}
            answer={item.a}
            isOpen={openId === item.id}
            onToggle={() => setOpenId(openId === item.id ? null : item.id)}
          />
        ))}
      </div>

      <div
        className="mt-auto flex items-center gap-3 rounded-[10px] border px-3.5 py-2.5"
        style={{ backgroundColor: SURFACE_2, borderColor: BORDER_SOFT }}
      >
        <IconDisc source={ChatIcon} size={28} />
        <div className="min-w-0 flex-1">
          <p className="mb-0 text-[13px] font-semibold leading-tight" style={{ color: INK }}>
            {t('pricing_faq_help_title')}
          </p>
          <p className="mb-0 mt-0.5 text-[12px] leading-snug" style={{ color: MUTED }}>
            {t('pricing_faq_help_body')}
          </p>
        </div>
        <div className="flex-none">
          {/* openSupport opens mail. It used to open Crisp live chat, which has
              been removed - see src/support.js. */}
          <Button variant="primary" size="slim" onClick={openSupport}>
            {t('pricing_faq_help_button')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PricingTrustFaq({ t }) {
  return (
    <div className="mb-6 grid grid-cols-1 items-stretch gap-4 md:grid-cols-2">
      <TrustCard t={t} />
      <FaqCard t={t} />
    </div>
  );
}
